import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';

/**
 * One PUDO depot (parts pickup/drop-off point), flattened from the SN `pudo_location_list`
 * endpoint. Read-only: the app never writes depots back.
 *
 * `lat`/`lng` are *not* part of the ServiceNow payload — that endpoint returns no coordinates at
 * all — so they are filled in server-side by geocoding `street`/`zipCode`/`city` and are null for
 * any depot whose address Google can't resolve.
 */
@Entity()
export class PudoLocation extends AdornisEntity {
  static override _class = 'PudoLocation';

  // Same rationale as ServiceNowTicketPart: baseql refuses to merge an array of entities without
  // a resolvable id, and these rows are read-only and replaced wholesale on every fetch.
  static override resolveID(_entity: PudoLocation): string {
    return A.getGloballyUniqueID();
  }

  @Field(() => String)
  sysId: Maybe<string>;

  @Field(() => String)
  name: Maybe<string>;

  /** Carrier operating the depot (DHL, …). Blank for roughly a third of them. */
  @Field(() => String)
  company: Maybe<string>;

  @Field(() => String)
  street: Maybe<string>;

  @Field(() => String)
  zipCode: Maybe<string>;

  @Field(() => String)
  city: Maybe<string>;

  /** Free-text opening hours, e.g. `Mon-Fri 8h-19h`. Blank for roughly a third of the depots. */
  @Field(() => String)
  siteAccessHours: Maybe<string>;

  @Field(() => Float)
  lat: Maybe<number>;

  @Field(() => Float)
  lng: Maybe<number>;
}

/**
 * Geocoding cache for PUDO depots, mirroring {@link TicketLocationCache} but keyed by the depot's
 * ServiceNow `sysId`. Worth caching harder than tickets: the depot list is ~100 rows that barely
 * change, and without a cache every open of the map view would burn 100 Google geocode calls.
 *
 * Invalidated by comparing the stored `addressLabel` against the one composed from the current
 * payload, so an edited depot address re-geocodes on its own.
 */
@Entity()
export class PudoLocationCache extends MongoEntity {
  static override _class = 'PudoLocationCache';
  static override _collectionName = 'smartteams_pudo_location_cache';

  @Field(() => String)
  sysId: Maybe<string>;

  @Field(() => String)
  addressLabel: Maybe<string>;

  @Field(() => Float)
  lat: Maybe<number>;

  @Field(() => Float)
  lng: Maybe<number>;

  @Field(() => Date, { default: v => v ?? new Date() })
  geocodedAt: Maybe<Date>;
}

/** One colleague field engineer, for the "select FE" picker above the colleague list/map. */
@Entity()
export class ColleagueEngineer extends AdornisEntity {
  static override _class = 'ColleagueEngineer';

  static override resolveID(_entity: ColleagueEngineer): string {
    return A.getGloballyUniqueID();
  }

  /** ServiceNow `sys_id` — what `assigned_to` is matched on. */
  @Field(() => String)
  sysId: Maybe<string>;

  @Field(() => String)
  name: Maybe<string>;

  @Field(() => String)
  email: Maybe<string>;
}
